// Inspection exercises use published workflows, not invented strategy results.
const GUIDE_WORKFLOW_UPDATED_AT = '2026-10-02';
const GUIDE_WORKFLOW_EXAMPLES = {
  '/guides/automated-trading-platforms': {
    heading: 'Apply the checklist to a real workflow',
    paragraphs: [
      'The external research feed case study describes dated snapshots, rebalancing rules and a stale-data guard. Use it to distinguish an algorithm engine from the data collection and operational work around that engine.',
    ],
    bullets: [
      'List who supplies the dated research feed, historical prices and broker connection.',
      'Check where the rules, logs and stop control are inspected.',
      'Compare the same workflow against the documented scope of another platform; do not infer support from a marketing label.',
    ],
  },
  '/guides/how-to-build-an-ai-trading-bot': {
    heading: 'Inspect a published example before drafting your own',
    paragraphs: [
      'The external research feed case study links a public marketplace strategy. Open its listing, read the publisher’s rules, and clone it only if you want to inspect a copy in your own BotSpot workspace. Cloning does not start a bot.',
    ],
    bullets: [
      'Ask the AI Agent: “Explain the entry, exit, sizing, evaluation schedule and stale-data behavior of my copied strategy. Identify anything the code does not specify. Do not change or deploy it.”',
      'Compare the explanation with the code before writing a new prompt.',
      'Preserve your own rules and inspection notes; the public example is not evidence that your version will profit.',
    ],
    links: [{ label: 'Browse published strategy templates', url: '/marketplace' }],
  },
  '/guides/algorithmic-trading-without-coding': {
    heading: 'Review the logic AI wrote for you',
    paragraphs: [
      'Use the AI trading team case study to separate a plain-English decision from the generated trading implementation. The workflow illustration describes research, competing arguments and a trade decision; it does not independently prove an investment edge.',
    ],
    bullets: [
      'In your own strategy, ask the agent to map each written rule to the code that implements it.',
      'Check sizing, repeated evaluations and duplicate-order behavior before running the strategy.',
      'If a rule is absent, revise the specification rather than relying on the agent to guess.',
    ],
  },
  '/guides/backtesting-ai-trading-strategies': {
    heading: 'Trace one decision from rules to simulated output',
    paragraphs: [
      'The AI trading team case study includes workflow and backtest screenshots. Use them as an inspection starting point, not as a reproducible run or independently verified live history. For your own test, preserve the strategy revision, dates and resulting files together.',
    ],
    bullets: [
      'Open a completed backtest in your workspace and inspect a trade timestamp alongside the strategy rule.',
      'Ask the agent: “Explain the data available at this decision, the order timing and the cost assumptions. Identify missing evidence. Do not revise or deploy the strategy.”',
      'If the run lacks a required log or input, record that gap instead of filling it with an assumed value.',
    ],
  },
  '/guides/paper-trading-vs-live-trading': {
    heading: 'Check the actual connection mode before approving a run',
    paragraphs: [
      'BotSpot’s Tradier connection illustrates why authentication and trading mode must be checked together. The published source lists live mode for OAuth and paper or live mode for API-key connections. Other brokers have different modes.',
    ],
    bullets: [
      'Open the broker page and compare supported authentication methods with the intended account mode.',
      'Confirm the selected broker account and mode in BotSpot before approving a deployment or order.',
      'After a run, inspect order acceptance, fills and logs at both BotSpot and the broker; a simulated fill is not a live fill.',
    ],
    links: [
      { label: 'Check Tradier connection requirements', url: '/brokers/tradier' },
      { label: 'Explore all broker modes', url: '/brokers' },
    ],
  },
  '/guides/best-backtesting-software': {
    heading: 'Evaluate a backtester with one concrete inspection task',
    paragraphs: [
      'Use the same written strategy specification when evaluating products. The AI trading team case study shows a workflow illustration and backtest screenshots; use those to identify the artifacts you would need to inspect, without treating the screenshots as a cross-platform benchmark.',
    ],
    bullets: [
      'Check whether the product can expose the decision inputs, code or rules, order timestamps and trade output your workflow requires.',
      'Record unsupported data or execution assumptions before comparing summary metrics.',
      'Keep platform-fit judgments separate from claims about simulation accuracy or future investment performance.',
    ],
  },
  '/guides/backtesting-metrics-explained': {
    heading: 'Read the published fields on a strategy listing',
    paragraphs: [
      'Open a marketplace listing and separate its simulated backtest fields from any published live history. The catalog can contain missing metrics; missing drawdown or dates are evidence gaps, not zero risk.',
    ],
    bullets: [
      'Record the reported return, drawdown and test window together when they are available.',
      'Check whether the listing identifies the calculation inputs needed to interpret Sharpe or another ratio.',
      'Clone a strategy to run your own simulation if useful, but do not substitute that run for the publisher’s history.',
    ],
    links: [{ label: 'Inspect marketplace metric labels', url: '/marketplace' }],
  },
  '/guides/avoid-overfitting-trading-strategies': {
    heading: 'Keep a revision record instead of chasing the best screenshot',
    paragraphs: [
      'The historical swing-trading tutorial shows an RSI strategy followed by a revision. That sequence is useful for discussing tuning, but the images do not establish an unseen-period test or an independently validated result.',
    ],
    bullets: [
      'For your own strategy, record the reason for each rule change before testing it.',
      'Retain prior code revisions and run inputs, including weak results.',
      'Keep an untouched evaluation period and disclose all attempted alternatives when explaining the outcome.',
    ],
    links: [
      {
        label: 'Read the historical swing-trading walkthrough',
        url: '/blog/using-ai-to-make-ai-profitable-swing-trading-bot-in-seconds',
      },
    ],
  },
  '/guides/walk-forward-testing-trading-strategies': {
    heading: 'Make each evaluation window auditable',
    paragraphs: [
      'In your BotSpot workspace, keep the strategy revision and backtest date range for each run. A series of runs becomes a walk-forward study only when the training choices and later evaluation periods remain separated; changing dates alone does not establish that separation.',
    ],
    bullets: [
      'Write the window boundaries and parameter-selection rule before examining evaluation output.',
      'Use the frozen strategy revision for the next evaluation period and retain its run artifacts.',
      'Compare trade behavior and missing-data handling across windows before summarizing the study.',
    ],
    links: [
      {
        label: 'Follow the backtest inspection workflow',
        url: '/guides/backtesting-ai-trading-strategies',
      },
    ],
  },
  '/guides/look-ahead-bias-and-data-leakage': {
    heading: 'Inspect point-in-time research replay',
    paragraphs: [
      'The external research feed case study describes saving dated snapshots so a changing portfolio source can be replayed. The page describes the mechanism but does not supply a complete dated dataset for independent reproduction.',
    ],
    bullets: [
      'For your own run, compare a decision timestamp with the publication and availability times of its research inputs.',
      'Check that a later revision to a research feed cannot rewrite an earlier simulated decision.',
      'If a historical snapshot is unavailable, document the gap; do not replay today’s selections as though they were known earlier.',
    ],
  },
  '/guides/modeling-slippage-fees-and-market-impact': {
    heading: 'Compare the order model with the intended execution',
    paragraphs: [
      'The options income case study is a design walkthrough for screening, assignment and rolling rules, not a published backtest. It is a useful checklist for identifying execution assumptions that an options simulation must explain.',
    ],
    bullets: [
      'In your own strategy, inspect the order type and timing alongside the modeled fill price.',
      'Record whether spread, per-contract charges, assignment and rolling costs are modeled or omitted.',
      'Keep broker-reported fills distinct from simulated fills when comparing later behavior.',
    ],
  },
  '/guides/algorithmic-trading-risk-management': {
    heading: 'Inspect one operational guard in context',
    paragraphs: [
      'The external research feed case study describes a stale-data guard for rebalancing. Treat that as a specific failure condition to inspect; it is not a claim that the strategy eliminates market or software risk.',
    ],
    bullets: [
      'For your own strategy, find the code path that blocks an action when required inputs are unavailable.',
      'Check where the failure is visible in logs and how you stop the deployment.',
      'Review position sizing and exposure separately; a data freshness check does not limit every possible loss.',
    ],
  },
  '/guides/connect-chatgpt-to-your-broker': {
    heading: 'Verify the account boundary with a read-only request',
    paragraphs: [
      'After completing the linked ChatGPT setup, start with account inspection. BotSpot OAuth connects the client to BotSpot; the broker connection and account mode are managed separately inside BotSpot.',
    ],
    bullets: [
      'Ask: “Using BotSpot, list my connected broker and account mode. Do not place orders, change strategies or start deployments.”',
      'Compare the response with Broker Connections in BotSpot.',
      'For an order proposal, inspect the actual symbol, side, quantity, account and mode on the authorization page. Reject a mismatch rather than approving it.',
    ],
    links: [
      {
        label: 'Open the current ChatGPT setup instructions',
        url: '/agents#agents-client-chatgpt',
      },
      { label: 'Check supported broker modes', url: '/brokers' },
    ],
  },
  '/guides/connect-claude-to-your-broker': {
    heading: 'Test the connected Claude workflow without changing an account',
    paragraphs: [
      'Complete the setup for the exact Claude client you use. A remote Claude connector and a local Claude Code setup have different authentication instructions; use the corresponding section on the MCP setup page.',
    ],
    bullets: [
      'Ask Claude to list your BotSpot strategies without revising or deploying them.',
      'Inspect an existing backtest and ask which inputs or artifacts are missing.',
      'Check broker account and mode in BotSpot before approving any account-changing action; never put brokerage secrets into the conversation.',
    ],
    links: [{ label: 'Choose the current Claude client setup', url: '/agents' }],
  },
  '/guides/connect-codex-to-your-broker': {
    heading: 'Verify tool access separately from trading readiness',
    paragraphs: [
      'After configuring BotSpot in your client, use an inspection task before an account-changing request. Successful tool discovery does not establish broker permissions, asset eligibility or live-order behavior.',
    ],
    bullets: [
      'Ask the client to list existing strategies and explain one existing backtest without changing it.',
      'Compare the reported account mode with the visible BotSpot broker connection.',
      'Review any subsequent authorization request in BotSpot before approving it; inspect its account and action details.',
    ],
    links: [
      { label: 'Open the current Codex setup instructions', url: '/agents#agents-client-codex' },
    ],
  },
};

function withGuideWorkflowExample(route) {
  const example = GUIDE_WORKFLOW_EXAMPLES[route.path];
  if (!example) return route;
  const sections = [...route.content.sections];
  const riskIndex = sections.findIndex((section) => /risk|limitations/i.test(section.heading));
  sections.splice(riskIndex < 0 ? sections.length : riskIndex, 0, example);
  return {
    ...route,
    lastmod: GUIDE_WORKFLOW_UPDATED_AT,
    modifiedAt: GUIDE_WORKFLOW_UPDATED_AT,
    content: { ...route.content, sections },
  };
}

module.exports = { GUIDE_WORKFLOW_EXAMPLES, GUIDE_WORKFLOW_UPDATED_AT, withGuideWorkflowExample };
